import { supabase } from "./supabase";
import type { Bloco } from "./conteudo";

// O IO do curso que MORA no hub (000375): o primeiro é o Fundamentos da Reforma Tributária, o low
// ticket que veio da Lovable. ZERO regra: quem diz se a aula está aberta, quantas contam, qual é a
// próxima e se o certificado sai é o banco (`curso_situacao`, um lugar só). Nenhum `select` cru: as
// tabelas do curso têm `revoke all`, e a leitura sai por função que pergunta a `conteudo_liberado`.
//
// Genérico por CARD, como o motor de conteúdo: o curso seguinte que morar aqui entra sem mudar
// nada deste arquivo.

export type Papel = "aula" | "bonus" | "encerramento";

export type AulaNoPainel = {
  slug: string;
  nome: string;
  papel: Papel | null;
  duracao: string | null;
  temVideo: boolean;
  linkExterno: string | null;
  liberada: boolean;
  concluida: boolean;
  temAnotacao: boolean;
};

export type ModuloNoPainel = {
  slug: string;
  nome: string;
  /** O papel de TODAS as páginas do módulo, quando é um só (o bônus, o certificado); senão nulo. */
  papel: Papel | null;
  liberado: boolean;
  concluido: boolean;
  aulas: AulaNoPainel[];
};

export type Painel = {
  cursoNome: string;
  total: number;
  concluidas: number;
  proxima: { secao: string; pagina: string; nome: string; secaoNome: string } | null;
  certificado: { liberado: boolean; emitidoEm: string | null };
  modulos: ModuloNoPainel[];
};

export type Leitura<T> = { fase: "pronto"; dados: T } | { fase: "sem-acesso" } | { fase: "erro" };

const texto = (v: unknown) => (typeof v === "string" && v !== "" ? v : null);
const papel = (v: unknown): Papel | null => (v === "aula" || v === "bonus" || v === "encerramento" ? v : null);

export async function lerPainel(card: string): Promise<Leitura<Painel>> {
  const { data, error } = await supabase.rpc("curso_painel", { p_card: card });
  if (error) return { fase: "erro" };
  const r = (data ?? {}) as Record<string, unknown>;
  if (r.permitido !== true) return { fase: "sem-acesso" };
  const proxima = r.proxima as Record<string, unknown> | null;
  const cert = (r.certificado ?? {}) as Record<string, unknown>;
  return {
    fase: "pronto",
    dados: {
      cursoNome: String(r.curso_nome ?? ""),
      total: Number(r.total ?? 0),
      concluidas: Number(r.concluidas ?? 0),
      proxima: proxima
        ? {
            secao: String(proxima.secao),
            pagina: String(proxima.pagina),
            nome: String(proxima.nome ?? ""),
            secaoNome: String(proxima.secao_nome ?? ""),
          }
        : null,
      certificado: { liberado: cert.liberado === true, emitidoEm: texto(cert.emitido_em) },
      modulos: (Array.isArray(r.modulos) ? r.modulos : []).map((bruto) => {
        const m = bruto as Record<string, unknown>;
        return {
          slug: String(m.slug),
          nome: String(m.nome ?? ""),
          papel: papel(m.papel),
          liberado: m.liberado === true,
          concluido: m.concluido === true,
          aulas: (Array.isArray(m.aulas) ? m.aulas : []).map((b) => {
            const a = b as Record<string, unknown>;
            return {
              slug: String(a.slug),
              nome: String(a.nome ?? ""),
              papel: papel(a.papel),
              duracao: texto(a.duracao),
              temVideo: a.tem_video === true,
              linkExterno: texto(a.link_externo),
              liberada: a.liberada === true,
              concluida: a.concluida === true,
              temAnotacao: a.tem_anotacao === true,
            };
          }),
        };
      }),
    },
  };
}

export type Vizinha = { secao: string; pagina: string; nome: string; liberada: boolean } | null;

export type Aula =
  | { existe: false }
  | {
      existe: true;
      liberada: false;
      nome: string;
      secaoNome: string;
      papel: Papel | null;
      /** A frase do banco: o que falta para abrir. */
      mensagem: string;
    }
  | {
      existe: true;
      liberada: true;
      nome: string;
      secaoNome: string;
      papel: Papel | null;
      scaleupId: string | null;
      duracao: string | null;
      linkExterno: string | null;
      concluida: boolean;
      anotacao: string | null;
      blocos: Bloco[];
      anterior: Vizinha;
      proxima: Vizinha;
    };

function vizinha(v: unknown): Vizinha {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  return { secao: String(o.secao), pagina: String(o.pagina), nome: String(o.nome ?? ""), liberada: o.liberada === true };
}

export async function lerAula(card: string, secao: string, pagina: string): Promise<Leitura<Aula>> {
  const { data, error } = await supabase.rpc("curso_aula", { p_card: card, p_secao: secao, p_pagina: pagina });
  if (error) return { fase: "erro" };
  const r = (data ?? {}) as Record<string, unknown>;
  if (r.permitido !== true) return { fase: "sem-acesso" };
  if (r.existe !== true) return { fase: "pronto", dados: { existe: false } };
  const a = (r.aula ?? {}) as Record<string, unknown>;
  const base = { nome: String(a.nome ?? ""), secaoNome: String(a.secao_nome ?? ""), papel: papel(a.papel) };
  if (r.liberada !== true) {
    return { fase: "pronto", dados: { existe: true, liberada: false, ...base, mensagem: String(r.mensagem ?? "") } };
  }
  return {
    fase: "pronto",
    dados: {
      existe: true,
      liberada: true,
      ...base,
      scaleupId: texto(a.scaleup_id),
      duracao: texto(a.duracao),
      linkExterno: texto(a.link_externo),
      concluida: a.concluida === true,
      anotacao: texto(a.anotacao),
      blocos: (Array.isArray(r.blocos) ? r.blocos : []) as Bloco[],
      anterior: vizinha(r.anterior),
      proxima: vizinha(r.proxima),
    },
  };
}

/** O resultado de uma escrita: ok, ou a frase do banco. Nunca lança. */
export type Escrita = { ok: true } | { ok: false; mensagem: string };

async function escrever(fn: string, args: Record<string, unknown>): Promise<Escrita & Record<string, unknown>> {
  const { data, error } = await supabase.rpc(fn, args);
  // O erro de transporte e o somente leitura do «ver como» (000335) chegam aqui; a frase do
  // segundo já vem traduzida por `lib/verComo.ts`.
  if (error) return { ok: false, mensagem: error.message || "Não conseguimos gravar agora. Tente de novo." };
  const r = (data ?? {}) as Record<string, unknown>;
  if (r.ok !== true) return { ok: false, mensagem: String(r.mensagem ?? "Não conseguimos gravar agora."), ...r };
  return { ...r, ok: true };
}

/**
 * Registra que o aluno abriu a aula — é o "onde parei". Dispara e não espera: a falha do
 * registro não pode segurar a aula de ninguém (o desenho de `lib/uso.ts`).
 */
export function registrarAberturaDaAula(card: string, secao: string, pagina: string) {
  // O `.then` não é enfeite: o construtor do supabase-js só MANDA a requisição quando alguém o
  // consome. Com `void supabase.rpc(...)` nada saía, e a prova de tela pegou (30/10).
  supabase
    .rpc("curso_registrar_abertura", { p_card: card, p_secao: secao, p_pagina: pagina })
    .then(({ error }) => {
      if (error) console.warn("a abertura desta aula não foi registrada:", error.message);
    });
}

export async function concluirAula(card: string, secao: string, pagina: string, concluida: boolean): Promise<Escrita> {
  return escrever("curso_concluir_aula", { p_card: card, p_secao: secao, p_pagina: pagina, p_concluida: concluida });
}

export async function anotarAula(card: string, secao: string, pagina: string, conteudo: string): Promise<Escrita> {
  return escrever("curso_anotar", { p_card: card, p_secao: secao, p_pagina: pagina, p_texto: conteudo });
}

export type Certificado =
  | { ok: true; nome: string; cursoNome: string; data: string }
  | { ok: false; mensagem: string };

/**
 * O certificado. A primeira chamada registra a emissão, e a data IMPRESSA é a do dia em que ele é
 * aberto (`data`, o dia de hoje em São Paulo, dito pelo banco), como na Lovable: decisão do Enzo
 * em 30/10 (000378), que reverteu a data da primeira emissão da 000375. Ela vem no formato
 * AAAA-MM-DD e se LÊ, sem passar por `Date` (a cicatriz da data da colação, um dia antes).
 */
export async function pedirCertificado(card: string): Promise<Certificado> {
  const r = await escrever("curso_certificado", { p_card: card });
  if (!r.ok) return { ok: false, mensagem: r.mensagem };
  return {
    ok: true,
    nome: String(r.nome ?? ""),
    cursoNome: String(r.curso_nome ?? ""),
    data: String(r.data ?? ""),
  };
}
